import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { DESCRIPTION, NAME } from "./consts";
import { useNavigate } from "react-router-dom";
import { schema, formValues } from "./validations";
import {
  generateRandomUppercase,
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useAddRoleMutation } from "@/services";
import { toast } from "@/components";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useAdd = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      [DESCRIPTION]: "",
    },
  });

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const requiredFields = getRequiredFields(schema);

  const breadcrumbData = [
    {
      link: `/${routes.admin}/${routes.masterData}/${routes.role}`,
      name: `Roles`,
    },
    {
      link: "",
      name: "Add",
    },
  ];

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}/${routes.role}`);
  };

  const { mutate: addInfo, isPending: isAddPending } = useAddRoleMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      toast({ message: "Role Added Successfully" });
      queryClient.invalidateQueries({
        queryKey: ["roles"],
      });
      onBackClick();
    },
  });

  const onSubmit = (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    addInfo({
      data: {
        ..._data,
        userType: loggedInUserData?.userType,
        code: generateRandomUppercase(),
      },
    });
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
  };
};

export default useAdd;
